// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/mission-execution/shared/ExecutionTimeline.jsx

import React from "react";

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function humanize(value, fallback = "—") {
  const raw = clean(value);
  if (!raw) return fallback;

  const normalized = raw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const lower = normalized.toLowerCase();

  const exactLabels = {
    soc2: "SOC2",
    nist: "NIST",
    iso: "ISO",
    hipaa: "HIPAA",
    ccpa: "CCPA",
    aml: "AML",
    kyc: "KYC",
    fda: "FDA",
    osha: "OSHA",
    sop: "SOP",
    aws: "AWS",
    s3: "S3",
    iam: "IAM",
    waf: "WAF",
    api: "API",
    cloudflare: "Cloudflare",
    okta: "Okta",
    "google workspace": "Google Workspace",
    "risk queue": "Risk Queue",
    "validation rerun": "Validation Rerun",
    "validation run": "Validation Run",
    "validation failed": "Validation Failed",
    "validation passed": "Validation Passed",
    "operator note added": "Operator Note Added",
    "status changed": "Status Changed",
    "due date changed": "Due Date Changed",
    "owner changed": "Owner Changed",
    "priority changed": "Priority Changed",
    "workflow event": "Workflow Event",
    "evidence linked": "Evidence Linked",
    "decision feedback": "Decision Feedback",
    "recommendation accepted": "Recommendation Accepted",
  };

  if (exactLabels[lower]) return exactLabels[lower];

  return normalized
    .split(" ")
    .map((word) => {
      const key = word.toLowerCase();
      if (exactLabels[key]) return exactLabels[key];
      if (/^cc\d+(?:\.\d+)?$/i.test(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function eventTitle(event) {
  return (
    clean(event?.title) ||
    humanize(event?.event_type, "") ||
    humanize(event?.type, "") ||
    humanize(event?.category, "") ||
    "Timeline Event"
  );
}

function eventMessage(event) {
  return (
    clean(event?.message) ||
    clean(event?.metadata?.message) ||
    clean(event?.metadata?.note) ||
    clean(event?.metadata?.reason) ||
    clean(event?.reason) ||
    clean(event?.description) ||
    "No event message recorded."
  );
}

function eventTimeRaw(event) {
  return clean(event?.occurred_at || event?.created_at || event?.timestamp || event?.event_time || event?.updated_at);
}

function eventTime(event) {
  const raw = eventTimeRaw(event);
  if (!raw) return "Time not recorded";

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString();
}

function eventActor(event) {
  return (
    clean(event?.actor_name) ||
    clean(event?.actor_email) ||
    clean(event?.actor_id) ||
    clean(event?.user_name) ||
    clean(event?.triggered_by_name) ||
    clean(event?.triggered_by_user_id) ||
    clean(event?.metadata?.actor_name) ||
    clean(event?.metadata?.actor_email) ||
    clean(event?.metadata?.user_name) ||
    "OASYS"
  );
}

function eventSource(event) {
  const explicit = clean(event?.source || event?.metadata?.source || event?.event_source);
  const haystack = `${explicit} ${clean(event?.event_type)} ${clean(event?.type)} ${clean(event?.category)} ${clean(event?.title)} ${clean(event?.message)}`.toLowerCase();

  if (haystack.includes("validation")) return { label: "Validation", tone: "warn" };
  if (haystack.includes("workflow")) return { label: "Workflow", tone: "ok" };
  if (haystack.includes("evidence")) return { label: "Evidence", tone: "default" };
  if (haystack.includes("sop")) return { label: "SOP", tone: "warn" };
  if (haystack.includes("decision") || haystack.includes("recommendation") || haystack.includes("advisory")) return { label: "Decision", tone: "ok" };
  if (haystack.includes("operator") || haystack.includes("note")) return { label: "Operator", tone: "neutral" };
  if (haystack.includes("block")) return { label: "Blocked", tone: "danger" };
  if (haystack.includes("status") || haystack.includes("task") || haystack.includes("work")) return { label: "Mission Execution", tone: "default" };

  return { label: "Mission Execution", tone: "neutral" };
}

function eventSeverityTone(event) {
  const haystack = `${clean(event?.event_type)} ${clean(event?.type)} ${clean(event?.title)} ${clean(event?.message)} ${clean(event?.status)} ${clean(event?.result)}`.toLowerCase();

  if (haystack.includes("fail") || haystack.includes("block") || haystack.includes("reject") || haystack.includes("error")) return "danger";
  if (haystack.includes("pass") || haystack.includes("complete") || haystack.includes("accept") || haystack.includes("linked")) return "ok";
  if (haystack.includes("validation") || haystack.includes("warning") || haystack.includes("pending")) return "warn";
  return "neutral";
}

function sortEvents(events) {
  return [...events].sort((left, right) => {
    const leftTime = new Date(eventTimeRaw(left) || 0).getTime();
    const rightTime = new Date(eventTimeRaw(right) || 0).getTime();
    const safeLeft = Number.isFinite(leftTime) ? leftTime : 0;
    const safeRight = Number.isFinite(rightTime) ? rightTime : 0;
    return safeRight - safeLeft;
  });
}

const timelineWrapStyle = {
  display: "grid",
  gap: 12,
  minWidth: 0,
  paddingRight: 2,
};

const eventCardStyle = {
  position: "relative",
  minHeight: 82,
  border: "1px solid rgba(96, 165, 250, 0.24)",
  borderRadius: 16,
  padding: "14px 16px 14px 20px",
  background: "rgba(15, 23, 42, 0.42)",
  display: "grid",
  gap: 8,
  alignContent: "start",
  overflow: "visible",
  minWidth: 0,
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  minWidth: 0,
};

const eventAccentBaseStyle = {
  position: "absolute",
  inset: "0 auto 0 0",
  width: 3,
  borderRadius: "16px 0 0 16px",
};

const accentByTone = {
  ok: "linear-gradient(180deg, rgba(52, 211, 153, 0.88), rgba(52, 211, 153, 0.28))",
  warn: "linear-gradient(180deg, rgba(250, 204, 21, 0.88), rgba(250, 204, 21, 0.28))",
  danger: "linear-gradient(180deg, rgba(248, 113, 113, 0.90), rgba(248, 113, 113, 0.28))",
  neutral: "linear-gradient(180deg, rgba(148, 163, 184, 0.72), rgba(148, 163, 184, 0.24))",
  default: "linear-gradient(180deg, rgba(96, 165, 250, 0.88), rgba(96, 165, 250, 0.28))",
};

const titleStyle = {
  display: "block",
  color: "#f8fafc",
  fontSize: 14,
  lineHeight: 1.35,
  fontWeight: 900,
  overflowWrap: "anywhere",
  minWidth: 0,
};

const chipBaseStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 26,
  padding: "5px 10px",
  borderRadius: 999,
  fontSize: 11,
  lineHeight: 1,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const chipByTone = {
  ok: {
    color: "#bbf7d0",
    border: "1px solid rgba(52, 211, 153, 0.42)",
    background: "rgba(6, 78, 59, 0.42)",
  },
  warn: {
    color: "#fde68a",
    border: "1px solid rgba(250, 204, 21, 0.42)",
    background: "rgba(113, 63, 18, 0.34)",
  },
  danger: {
    color: "#fecaca",
    border: "1px solid rgba(248, 113, 113, 0.42)",
    background: "rgba(127, 29, 29, 0.32)",
  },
  neutral: {
    color: "#cbd5e1",
    border: "1px solid rgba(148, 163, 184, 0.32)",
    background: "rgba(51, 65, 85, 0.44)",
  },
  default: {
    color: "#bfdbfe",
    border: "1px solid rgba(96, 165, 250, 0.38)",
    background: "rgba(30, 64, 175, 0.28)",
  },
};

const messageStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.5,
  margin: 0,
  overflowWrap: "anywhere",
  minWidth: 0,
};

const metaStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  alignItems: "center",
  color: "#93c5fd",
  fontSize: 12,
  lineHeight: 1.35,
  minWidth: 0,
};

export default function ExecutionTimeline({ events = [], newestFirst = true }) {
  const rows = Array.isArray(events) ? events.filter(Boolean) : [];
  const orderedRows = newestFirst ? sortEvents(rows) : rows;

  if (!orderedRows.length) {
    return (
      <div style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.5 }}>
        No timeline events are available for this task yet.
      </div>
    );
  }

  return (
    <div style={timelineWrapStyle}>
      {orderedRows.map((event, index) => {
        const source = eventSource(event);
        const severityTone = eventSeverityTone(event);
        const chipTone = source.tone || severityTone || "default";

        return (
          <article
            key={clean(event?.id || event?.event_id || event?.validation_run_id || `${eventTitle(event)}-${index}`)}
            style={eventCardStyle}
          >
            <div
              style={{
                ...eventAccentBaseStyle,
                background: accentByTone[severityTone] || accentByTone.default,
              }}
            />
            <div style={headerStyle}>
              <strong style={titleStyle}>{eventTitle(event)}</strong>
              <span style={{ ...chipBaseStyle, ...(chipByTone[chipTone] || chipByTone.default) }}>
                {source.label}
              </span>
            </div>
            <p style={messageStyle}>{eventMessage(event)}</p>
            <div style={metaStyle}>
              <span>{eventActor(event)}</span>
              <span aria-hidden="true">·</span>
              <span>{eventTime(event)}</span>
            </div>
          </article>
        );
      })}
    </div>
  );
}
